import React, { useRef, useState, useCallback } from "react";

type ButtonVariant = "primary" | "outline" | "ghost";
type ButtonSize = "sm" | "md" | "lg";

interface Ripple {
  x: number;
  y: number;
  id: number;
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  href?: string;
  target?: string;
  rel?: string;
  ripple?: boolean;
  loading?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-bg-primary border border-accent hover:bg-accent/90 hover:shadow-glow-sm",
  outline:
    "bg-transparent text-accent border border-accent/30 hover:border-accent hover:shadow-glow-sm",
  ghost:
    "bg-transparent text-text-secondary border border-transparent hover:text-text-primary hover:bg-bg-surface",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs tracking-wider rounded",
  md: "px-5 py-2.5 text-sm tracking-wider rounded-md",
  lg: "px-7 py-3 text-base tracking-widest rounded-md",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      href,
      target,
      rel,
      ripple = false,
      loading = false,
      disabled,
      className = "",
      children,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLButtonElement>(null);
    const buttonRef = (ref as React.RefObject<HTMLButtonElement>) || innerRef;
    const [ripples, setRipples] = useState<Ripple[]>([]);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled || loading) return;

        const prefersReducedMotion =
          typeof window !== "undefined" &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (ripple && !prefersReducedMotion) {
          const btn = e.currentTarget;
          const rect = btn.getBoundingClientRect();
          const newRipple: Ripple = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
            id: Date.now(),
          };
          setRipples((prev) => [...prev, newRipple]);
          setTimeout(() => {
            setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
          }, 600);
        }
        onClick?.(e);
      },
      [disabled, loading, ripple, onClick],
    );

    const classes = [
      "relative inline-flex items-center justify-center gap-2 font-semibold tracking-wider uppercase",
      "overflow-hidden select-none whitespace-nowrap",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
      "disabled:opacity-40 disabled:cursor-not-allowed",
      "transition-colors transition-shadow duration-200",
      variantClasses[variant],
      sizeClasses[size],
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const content = (
      <>
        {loading && (
          <svg
            className="w-4 h-4 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        )}
        {children}
        {ripples.map((r) => (
          <span
            key={r.id}
            className="absolute rounded-full pointer-events-none animate-ping opacity-40"
            style={{
              left: r.x,
              top: r.y,
              width: 0,
              height: 0,
              backgroundColor: "currentColor",
              transform: "translate(-50%, -50%)",
            }}
          />
        ))}
      </>
    );

    if (href) {
      const safeRel =
        target === "_blank" ? (rel ?? "noopener noreferrer") : rel;
      return (
        <a
          ref={buttonRef as unknown as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={safeRel}
          className={classes}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        ref={buttonRef}
        className={classes}
        disabled={disabled || loading}
        onClick={handleClick}
        {...rest}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = "Button";
